import { useEffect } from "react";

/** Adds .is-visible to any element with [data-reveal] when it enters viewport.
 *  Also watches for dynamically-added reveal elements (e.g. async-loaded lists
 *  like the press grid) so they don't stay stuck at opacity:0. */
export function useScrollReveal() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const noMotion = document.body.classList.contains("a11y-no-motion");
    if (noMotion) {
      const revealAll = () => document.querySelectorAll("[data-reveal]").forEach((el) => el.classList.add("is-visible"));
      revealAll();
      const mo = new MutationObserver(revealAll);
      mo.observe(document.body, { childList: true, subtree: true });
      return () => mo.disconnect();
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -10% 0px" }
    );
    const observeNew = () => {
      document.querySelectorAll("[data-reveal]:not(.is-visible)").forEach((el) => io.observe(el));
    };
    observeNew();
    // Re-scan when new reveal elements are added to the DOM (debounced via rAF).
    let raf = 0;
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(observeNew);
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { io.disconnect(); mo.disconnect(); cancelAnimationFrame(raf); };
  }, []);
}

/** Parallax based on the element's position within the viewport (not absolute
 *  scrollY), so it works for sections anywhere on the page — not just the hero.
 *  Offset is bounded to ±|factor|*100px so the image never reveals its edges. */
export function useParallax(ref, factor = -0.18) {
  useEffect(() => {
    if (document.body.classList.contains("a11y-no-motion")) {
      if (ref?.current) ref.current.style.transform = "";
      return;
    }
    let raf;
    const update = () => {
      const node = ref?.current;
      if (!node) return;            // node may mount later (e.g. after media loads)
      const rect = node.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // progress: 0 when the element's centre is at the viewport centre,
      // -1 when entering from the bottom, +1 when leaving past the top.
      const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
      const clamped = Math.max(-1, Math.min(1, progress));
      const offset = clamped * factor * 200;
      node.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
    };
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();
    // Also retry shortly after mount in case the node/image mounts asynchronously.
    const t = setTimeout(update, 400);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [ref, factor]);
}
